import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './Navbar';
import Footer from './components/Footer';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/common/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toast.css';
import healthService from './services/healthService';
import { HelmetProvider } from 'react-helmet-async';

// Lazy load components
const Main = lazy(() => import('./components/Main'));
const About = lazy(() => import('./components/About'));
const Blog = lazy(() => import('./components/Blog'));
const BlogPostForm = lazy(() => import('./components/BlogPostForm'));
const AdminBlog = lazy(() => import('./components/AdminBlog'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const RecentOrders = lazy(() => import('./components/admin/Orders/RecentOrders'));
const ContactMe = lazy(() => import('./components/ContactMe'));
const Classes = lazy(() => import('./components/Classes'));
const ClassForm = lazy(() => import('./components/ClassForm'));
const Events = lazy(() => import('./components/Events'));
const EventForm = lazy(() => import('./components/EventForm'));
const Store = lazy(() => import('./components/Store'));
const StoreProfile = lazy(() => import('./components/StoreProfile'));
const Checkout = lazy(() => import('./components/Checkout'));
const AdminStoreManager = lazy(() => import('./components/admin/AdminStoreManager'));
const AdminOrdersManager = lazy(() => import('./components/admin/AdminOrdersManager'));
const ProductDetails = lazy(() => import('./components/ProductDetails'));
const DetailsPage = lazy(() => import('./components/DetailsPage'));
const AdminRegistrations = lazy(() => import('./components/AdminRegistrations'));
const InstructorManagement = lazy(() => import('./components/InstructorManagement'));
const AdminTestimonials = lazy(() => import('./components/AdminTestimonials'));
const MyTestimonials = lazy(() => import('./components/MyTestimonials'));
const ForgotPasswordPage = lazy(() => import('./components/ForgotPasswordPage'));
const ResetPasswordWithOtpPage = lazy(() => import('./components/ResetPasswordWithOtpPage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./components/TermsOfUse'));
const CancellationRefund = lazy(() => import('./components/CancellationRefund'));
const ShippingPolicy = lazy(() => import('./components/ShippingPolicy'));
const CancellationPolicy = lazy(() => import('./components/CancellationPolicy'));
const OrderConfirmed = lazy(() => import('./components/OrderConfirmed'));
const RegistrationForm = lazy(() => import('./components/RegistrationForm'));
const EventRegistrationForm = lazy(() => import('./components/EventRegistrationForm'));
const Payment = lazy(() => import('./components/Payment/Payment'));
const Orders = lazy(() => import('./components/Orders'));
const CMSAboutEditor = lazy(() => import('./components/admin/CMS/CMSAboutEditor'));
const CMSHeroMediaEditor = lazy(() => import('./components/admin/CMS/CMSHeroMediaEditor'));
const CMSTrainersEditor = lazy(() => import('./components/admin/CMS/CMSTrainersEditor'));
const FAQ = lazy(() => import('./components/FAQ'));
const OnlineClasses = lazy(() => import('./components/OnlineClasses'));
const OnlinePrivateClasses = lazy(() => import('./components/OnlinePrivateClasses'));
const OnlineGroupClasses = lazy(() => import('./components/OnlineGroupClasses'));

// ScrollToTop component
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      // If there's a hash, scroll to that element after a small delay
      const timer = setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // If no hash, scroll to top
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => {
    // Start the health check service when the app mounts
    healthService.startHealthCheck();

    // Cleanup function to stop the health check when the app unmounts
    return () => {
      healthService.stopHealthCheck();
    };
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <HelmetProvider>
        <AuthProvider>
          <div className="App">
            <Navbar />
            <ToastContainer
              position="bottom-right"
              autoClose={2000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable={false}
              pauseOnHover
              theme="dark"
              limit={4}
              transition={Slide}
            />
            <main className="main-content">
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/" element={<Main />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<Blog />} />

                  {/* User Profile Route */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <UserProfile />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Dashboard */}
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Blog Routes */}
                  <Route
                    path="/admin/blog"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminBlog />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/blog/new"
                    element={
                      <ProtectedRoute adminOnly>
                        <BlogPostForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/blog/edit/:id"
                    element={
                      <ProtectedRoute adminOnly>
                        <BlogPostForm />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Orders Routes */}
                  <Route
                    path="/admin/orders"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminOrdersManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/orders/recent"
                    element={
                      <ProtectedRoute adminOnly>
                        <RecentOrders />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Classes Routes */}
                  <Route
                    path="/admin/classes/new"
                    element={
                      <ProtectedRoute adminOnly>
                        <ClassForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/classes/edit/:id"
                    element={
                      <ProtectedRoute adminOnly>
                        <ClassForm />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Events Routes */}
                  <Route
                    path="/admin/events/new"
                    element={
                      <ProtectedRoute adminOnly>
                        <EventForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/events/edit/:id"
                    element={
                      <ProtectedRoute adminOnly>
                        <EventForm />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Store Routes */}
                  <Route
                    path="/admin/store"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminStoreManager />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Registrations Route */}
                  <Route
                    path="/admin/registrations"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminRegistrations />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Instructor Management Routes */}
                  <Route
                    path="/admin/instructors"
                    element={
                      <ProtectedRoute adminOnly>
                        <InstructorManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/instructors/new"
                    element={
                      <ProtectedRoute adminOnly>
                        <InstructorManagement />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Testimonials Route */}
                  <Route
                    path="/admin/testimonials"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminTestimonials />
                      </ProtectedRoute>
                    }
                  />

                  {/* CMS About Editor Route */}
                  <Route
                    path="/admin/cms/about"
                    element={
                      <ProtectedRoute adminOnly>
                        <CMSAboutEditor />
                      </ProtectedRoute>
                    }
                  />

                  {/* CMS Hero Media Editor Route */}
                  <Route
                    path="/admin/cms/hero-media"
                    element={
                      <ProtectedRoute adminOnly>
                        <CMSHeroMediaEditor />
                      </ProtectedRoute>
                    }
                  />

                  {/* CMS Trainers Editor Route */}
                  <Route
                    path="/admin/cms/trainers"
                    element={
                      <ProtectedRoute adminOnly>
                        <CMSTrainersEditor />
                      </ProtectedRoute>
                    }
                  />

                  {/* My Testimonials Route */}
                  <Route
                    path="/my-testimonials"
                    element={
                      <ProtectedRoute>
                        <MyTestimonials />
                      </ProtectedRoute>
                    }
                  />

                  {/* Public Routes */}
                  <Route path="/contactme" element={<ContactMe />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/classes" element={<Classes />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/online-classes" element={<OnlineClasses />} />
                  <Route path="/online-private-classes" element={<OnlinePrivateClasses />} />
                  <Route path="/online-group-classes" element={<OnlineGroupClasses />} />
                  <Route path="/store" element={<Store />} />
                  <Route path="/store-profile" element={
                    <ProtectedRoute>
                      <StoreProfile />
                    </ProtectedRoute>
                  } />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  } />
                  <Route path="/payment" element={
                    <ProtectedRoute>
                      <Payment />
                    </ProtectedRoute>
                  } />
                  <Route path="/orders" element={
                    <ProtectedRoute>
                      <Orders />
                    </ProtectedRoute>
                  } />
                  <Route path="/product/:id" element={<ProductDetails />} />

                  {/* Details Page Routes */}
                  <Route path="/learn-more/:type/:id" element={<DetailsPage />} />

                  {/* User Management Route */}
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute adminOnly>
                        <UserManagement />
                      </ProtectedRoute>
                    }
                  />

                  {/* Forgot Password Route */}
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  {/* Reset Password with OTP Route */}
                  <Route path="/reset-password-with-otp" element={<ResetPasswordWithOtpPage />} />

                  {/* Legal Routes */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/cancellation-refund" element={<CancellationRefund />} />
                  <Route path="/shipping-policy" element={<ShippingPolicy />} />
                  <Route path="/cancellation-policy" element={<CancellationPolicy />} />

                  {/* Order Confirmed Route */}
                  <Route path="/order-confirmed" element={<OrderConfirmed />} />

                  {/* Event Registration Form Route - Select Event/Class and Level */}
                  <Route
                    path="/event-registration"
                    element={
                      <ProtectedRoute>
                        <EventRegistrationForm />
                      </ProtectedRoute>
                    }
                  />

                  {/* Registration Route - Fill Details and Payment */}
                  <Route
                    path="/register"
                    element={
                      <ProtectedRoute>
                        <RegistrationForm />
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
        </AuthProvider>
      </HelmetProvider>
    </Router>
  );
}

export default App;